import { observer } from 'mobx-react-lite'
import Styles from './BlogMetadata.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { BlogIntegrationSelect } from 'components/ExternalIntegrationSelect/BlogIntegrationSelect'
import { AlertTriangle } from 'blaze-ui/icons'
import { TextInputField } from 'components/Form/TextInputField'
import { useState, useCallback, useEffect } from 'react'
import { useUpdateBlogMetadataRecord } from 'hooks/queries/blogMedatadataRecords/useUpdateBlogMetadataRecord'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'
import { TagInput } from 'components/TagInput/TagInput'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import debounce from 'lodash/debounce'
import { Input } from 'almanac-ui/TextInputField/Input'
import { FeaturedMediaSelectModal } from './components/media/FeaturedMediaSelectModal/FeaturedMediaSelectModal'
import { CategoryDropdown } from './components/taxonomy/CategoryDropDown/CategoryDropDown'
import { GoHighLevelBlogSelect } from './components/integrations/GoHighLevelBlogSelect/GoHighLevelBlogSelect'
import { GoHighLevelAuthorSelect } from './components/integrations/GoHighLevelAuthorSelect/GoHighLevelAuthorSelect'
import { useModals } from 'almanac-ui/Modal'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import cx from 'classnames'

import { FeaturedMediaImage } from './components/media/FeaturedMediaImage/FeaturedMediaImage'
import { WordPressProfile, GoHighLevelProfile, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { getInitialAltText } from './utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useArticlePostPreviewContext } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'

dayjs.extend(utc)
dayjs.extend(timezone)

type Props = {
  articleId: string
  initialData?: BlogMetadataRecord
}

export const BlogMetadata = observer(({ articleId, initialData }: Props) => {
  const { blogIntegrations } = useBlogIntegrations()
  const { openModal, closeModal } = useModals()
  const { scheduledPost } = useArticlePostPreviewContext()
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const updateMetadata = useUpdateBlogMetadataRecord()

  const { status } = usePostProperties({ id: articleId, type: AssigneeType.Article })
  const isPostPublished = status?.value === PostStatusPropertyValue.Posted

  const [title, setTitle] = useState<string>(initialData?.title || '')
  const [summary, setSummary] = useState<string>(initialData?.summary || '')
  const [slug, setSlug] = useState<string>(initialData?.url_slug || '')
  const [author, setAuthor] = useState<string>(initialData?.author || '')
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const [featuredImageUrl, setFeaturedImageUrl] = useState<string>(
    initialData?.featured_media?.current_featured_image ||
      initialData?.featured_media?.featured_selected_images?.[0]?.url ||
      '',
  )

  const [featuredImageAltText, setFeaturedImageAltText] = useState<string>(
    getInitialAltText(initialData as BlogMetadataRecord),
  )
  const [tags, setTags] = useState<string[]>(initialData?.tags ?? [])
  const [featuredImages, setFeaturedImages] = useState<string[]>(
    initialData?.featured_media?.featured_selected_images?.map((img) => img.url) ?? [],
  )
  const [availableCategories, setAvailableCategories] = useState<string[]>(() => {
    // Get the currently selected integration from the scheduled post
    const selectedIntegration = scheduledPost?.external_integration
    let platformCategories: string[] = []

    if (selectedIntegration) {
      // Show categories only from the selected integration
      if (selectedIntegration.provider === ExternalIntegrationEnum.WordPress) {
        const wpProfile = selectedIntegration.additional_data as WordPressProfile
        platformCategories = wpProfile?.wordpress_categories?.map((cat) => cat.name) ?? []
      } else if (selectedIntegration.provider === ExternalIntegrationEnum.GoHighLevel && goHighLevelEnabled) {
        const ghlProfile = selectedIntegration.additional_data as GoHighLevelProfile
        platformCategories = ghlProfile?.go_high_level_categories?.map((cat) => cat.name) ?? []
      }
    }

    // Add previously selected custom categories
    const previouslySelected = initialData?.categories ?? []
    const customCategories = previouslySelected.filter(
      (selectedCat) => !platformCategories.some((cat) => cat.toLowerCase() === selectedCat.toLowerCase()),
    )

    // Combine and deduplicate
    const finalCategories =
      platformCategories.length === 0
        ? ['Uncategorized', ...customCategories]
        : [...platformCategories, ...customCategories]

    return finalCategories.filter(
      (cat, index, arr) => arr.findIndex((c) => c.toLowerCase() === cat.toLowerCase()) === index,
    )
  })

  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categories ?? [])

  // GoHighLevel blog and author selection state
  const [selectedBlogId, setSelectedBlogId] = useState<string | undefined>(initialData?.selected_blog_id)
  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(initialData?.selected_author_id)

  // GoHighLevel users can only select from pre-defined categories, not add custom ones
  const allowAddingCategories = scheduledPost?.external_integration?.provider !== ExternalIntegrationEnum.GoHighLevel

  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { fileInputRef, uploadImagesFromEvent } = useImportMediaFiles(false, (files) => {
    if (files && files.length > 0 && files[0].source_url) {
      const newImageUrl = files[0].source_url

      // Add to featured images array
      const newImages = [...featuredImages, newImageUrl]
      setFeaturedImages(newImages)

      // Create new selected images array
      const newSelectedImages = newImages.map((url) => ({ url, alt_text: '' }))

      // Set as current featured image and update metadata
      handleUpdateMetadata({
        featured_media: {
          ...initialData?.featured_media,
          featured_selected_images: newSelectedImages,
          current_featured_image: newImageUrl,
        },
      })

      // Update local state
      setFeaturedImageUrl(newImageUrl)
      setFeaturedImageAltText('')
    }
  })

  const handleDirectUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (!uploadsProjectId) return
    if (event.target.files && event.target.files.length > 1) {
      const dt = new DataTransfer()
      dt.items.add(event.target.files[0])
      event.target.files = dt.files
    }
    uploadImagesFromEvent(event, uploadsProjectId)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const getAltTextForImage = (imageUrl: string): string => {
    const selectedImages = initialData?.featured_media?.featured_selected_images || []
    const imageObj = selectedImages.find((img) => img.url === imageUrl)
    return imageObj?.alt_text || ''
  }

  const updateAltTextForCurrentImage = (imageUrl: string) => {
    const altText = getAltTextForImage(imageUrl)
    setFeaturedImageAltText(altText)
  }

  const handleUpdateMetadata = useCallback(
    (updatedFields: Partial<BlogMetadataRecord>, overrides: Partial<BlogMetadataRecord> = {}) => {
      const newData = {
        ...initialData,
        ...updatedFields,
        title: overrides.title ?? title,
        author: overrides.author ?? author,
        summary: overrides.summary ?? summary,
        tags: overrides.tags ?? tags,
        url_slug: overrides.url_slug ?? slug,
        categories: overrides.categories ?? selectedCategories,
        selected_blog_id: overrides.selected_blog_id ?? updatedFields.selected_blog_id ?? selectedBlogId,
        selected_author_id: overrides.selected_author_id ?? updatedFields.selected_author_id ?? selectedAuthorId,
        featured_media: {
          ...initialData?.featured_media,
          ...updatedFields.featured_media,
        },
      }

      // Update local state to stay in sync
      if (updatedFields.featured_media?.current_featured_image) {
        setFeaturedImageUrl(updatedFields.featured_media.current_featured_image)
      }
      if (updatedFields.featured_media?.featured_selected_images) {
        setFeaturedImages(updatedFields.featured_media.featured_selected_images.map((img) => img.url))
      }

      // Let the mutation hook handle cache updates to avoid race conditions
      updateMetadata.mutate(newData)
    },
    [
      initialData,
      title,
      author,
      summary,
      slug,
      tags,
      selectedCategories,
      selectedBlogId,
      selectedAuthorId,
      updateMetadata,
    ],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateMetadata = useCallback(
    debounce((fields, overrides) => {
      handleUpdateMetadata(fields, overrides)
    }, 1000),
    [title, author, summary, slug, tags, articleId, initialData, featuredImages, selectedBlogId, selectedAuthorId],
  )

  const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    setTitle(newTitle)
    debouncedUpdateMetadata({ title: newTitle }, { title: newTitle })
  }

  const handleChangeSummary = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSummary = e.target.value
    setSummary(newSummary)
    debouncedUpdateMetadata({ summary: newSummary }, { summary: newSummary })
  }

  const handleChangeSlug = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value
    setSlug(newSlug)
    debouncedUpdateMetadata({ url_slug: newSlug }, { url_slug: newSlug })
  }

  const handleChangeAuthor = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAuthor = e.target.value
    setAuthor(newAuthor)
    debouncedUpdateMetadata({ author: newAuthor }, { author: newAuthor })
  }

  const handleChangeFeaturedImageAlt = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFeaturedImageAlt = e.target.value
    setFeaturedImageAltText(newFeaturedImageAlt)

    const selectedImages = initialData?.featured_media?.featured_selected_images || []
    const updatedSelectedImages = selectedImages.map((img) => {
      return img.url === featuredImageUrl ? { ...img, alt_text: newFeaturedImageAlt } : img
    })

    handleUpdateMetadata({
      featured_media: {
        ...initialData?.featured_media,
        alt_text: newFeaturedImageAlt,
        featured_selected_images: updatedSelectedImages,
      },
    })
  }

  const handleTagsChange = (newTags: string[]) => {
    setTags(newTags)
    handleUpdateMetadata({ tags: newTags }, { tags: newTags })
  }

  const handleCategoriesChange = (newCategories: string[]) => {
    setSelectedCategories(newCategories)
  }

  const handleCategoryDropdownClose = (selectedCategories: string[], updatedAvailableCategories: string[]) => {
    setAvailableCategories(updatedAvailableCategories)
    handleUpdateMetadata({ categories: selectedCategories })
  }

  const handleBlogSelect = (blogId: string) => {
    setSelectedBlogId(blogId)
    handleUpdateMetadata({ selected_blog_id: blogId })
  }

  const handleAuthorSelect = (authorId: string) => {
    setSelectedAuthorId(authorId)
    handleUpdateMetadata({ selected_author_id: authorId })
  }

  const handleOpenFeaturedMediaModal = () => {
    openModal(FeaturedMediaSelectModal, {
      images: featuredImages,
      selectedImage: featuredImageUrl,
      onSelect: (img: string) => {
        // Only add the image if it's not already in the array
        if (!featuredImages.includes(img)) {
          const newImages = [...featuredImages, img]
          // Get existing selected images to preserve their alt text
          const existingSelectedImages = initialData?.featured_media?.featured_selected_images || []

          // Create new selected images array, preserving existing alt text
          const newSelectedImages = newImages.map((url) => {
            const existingImage = existingSelectedImages.find((imgObj) => imgObj.url === url)
            return existingImage || { url, alt_text: '' }
          })

          handleUpdateMetadata({
            featured_media: {
              ...initialData?.featured_media,
              featured_selected_images: newSelectedImages,
              current_featured_image: img,
            },
          })
        } else {
          // Image already exists, just update the current featured image
          handleUpdateMetadata({
            featured_media: {
              ...initialData?.featured_media,
              current_featured_image: img,
            },
          })
        }
        setFeaturedImageUrl(img)
        // Update alt text to match the selected image
        updateAltTextForCurrentImage(img)
        closeModal()
      },
      onClose: closeModal,
      onImageUpload: (imgUrl: string) => {
        // Only add the image if it's not already in the array
        if (!featuredImages.includes(imgUrl)) {
          const newImages = [...featuredImages, imgUrl]
          setFeaturedImages(newImages)

          // Get existing selected images to preserve their alt text
          const existingSelectedImages = initialData?.featured_media?.featured_selected_images || []

          // Create new selected images array, preserving existing alt text
          const newSelectedImages = newImages.map((url) => {
            const existingImage = existingSelectedImages.find((imgObj) => imgObj.url === url)
            return existingImage || { url, alt_text: '' }
          })

          // Update metadata with the new images array - don't automatically set as current
          handleUpdateMetadata({
            featured_media: {
              ...initialData?.featured_media,
              featured_selected_images: newSelectedImages,
            },
          })
        }
      },
    })
  }

  useEffect(() => {
    if (initialData?.featured_media?.current_featured_image) {
      setFeaturedImageUrl(initialData.featured_media.current_featured_image)
    }
  }, [initialData?.featured_media?.current_featured_image])

  useEffect(() => {
    if (initialData?.featured_media?.featured_selected_images) {
      setFeaturedImages(initialData.featured_media.featured_selected_images.map((img) => img.url))
    }
  }, [initialData?.featured_media?.featured_selected_images])

  useEffect(() => {
    setSelectedCategories(initialData?.categories ?? [])
  }, [initialData?.categories])

  useEffect(() => {
    setSelectedBlogId(initialData?.selected_blog_id)
  }, [initialData?.selected_blog_id])

  useEffect(() => {
    setSelectedAuthorId(initialData?.selected_author_id)
  }, [initialData?.selected_author_id])

  // Update available categories when the selected integration changes
  useEffect(() => {
    const selectedIntegration = scheduledPost?.external_integration
    let platformCategories: string[] = []

    if (selectedIntegration) {
      if (selectedIntegration.provider === ExternalIntegrationEnum.WordPress) {
        const wpProfile = selectedIntegration.additional_data as WordPressProfile
        platformCategories = wpProfile?.wordpress_categories?.map((cat) => cat.name) ?? []
      } else if (selectedIntegration.provider === ExternalIntegrationEnum.GoHighLevel && goHighLevelEnabled) {
        const ghlProfile = selectedIntegration.additional_data as GoHighLevelProfile
        platformCategories = ghlProfile?.go_high_level_categories?.map((cat) => cat.name) ?? []
      }
    }

    // Add previously selected custom categories
    const previouslySelected = initialData?.categories ?? []
    const customCategories = previouslySelected.filter(
      (selectedCat) => !platformCategories.some((cat) => cat.toLowerCase() === selectedCat.toLowerCase()),
    )

    // Combine and deduplicate
    const finalCategories =
      platformCategories.length === 0
        ? ['Uncategorized', ...customCategories]
        : [...platformCategories, ...customCategories]

    const updatedCategories = finalCategories.filter(
      (cat, index, arr) => arr.findIndex((c) => c.toLowerCase() === cat.toLowerCase()) === index,
    )

    setAvailableCategories(updatedCategories)
  }, [scheduledPost?.external_integration, initialData?.categories, goHighLevelEnabled])

  // Backfill blog and author IDs when Go High Level integration is added or changes
  useEffect(() => {
    const selectedIntegration = scheduledPost?.external_integration

    // Only proceed if this is a Go High Level integration and the feature is enabled
    if (
      !selectedIntegration ||
      selectedIntegration.provider !== ExternalIntegrationEnum.GoHighLevel ||
      !goHighLevelEnabled
    ) {
      return
    }

    const ghlProfile = selectedIntegration.additional_data as GoHighLevelProfile
    if (!ghlProfile) {
      return
    }

    let shouldUpdateMetadata = false
    const updates: Partial<BlogMetadataRecord> = {}

    // Check if we need to backfill selected_blog_id
    if (!selectedBlogId && ghlProfile.selected_blog_id) {
      // Use the integration's default blog ID
      setSelectedBlogId(ghlProfile.selected_blog_id)
      updates.selected_blog_id = ghlProfile.selected_blog_id
      shouldUpdateMetadata = true
    } else if (!selectedBlogId && ghlProfile.blogs && ghlProfile.blogs.length === 1) {
      // Auto-select if only one blog is available
      const singleBlogId = ghlProfile.blogs[0].id
      setSelectedBlogId(singleBlogId)
      updates.selected_blog_id = singleBlogId
      shouldUpdateMetadata = true
    }

    // Check if we need to backfill selected_author_id
    if (!selectedAuthorId && ghlProfile.selected_author_id) {
      // Use the integration's default author ID
      setSelectedAuthorId(ghlProfile.selected_author_id)
      updates.selected_author_id = ghlProfile.selected_author_id
      shouldUpdateMetadata = true
    } else if (!selectedAuthorId && ghlProfile.authors && ghlProfile.authors.length === 1) {
      // Auto-select if only one author is available
      const singleAuthorId = ghlProfile.authors[0].id
      setSelectedAuthorId(singleAuthorId)
      updates.selected_author_id = singleAuthorId
      shouldUpdateMetadata = true
    }

    // Update the metadata record if we made changes
    if (shouldUpdateMetadata) {
      handleUpdateMetadata(updates)
    }
  }, [scheduledPost?.external_integration, goHighLevelEnabled, selectedBlogId, selectedAuthorId, handleUpdateMetadata])

  const selectedIntegration = scheduledPost?.external_integration
  const isGoHighLevelIntegration =
    selectedIntegration?.provider === ExternalIntegrationEnum.GoHighLevel && goHighLevelEnabled
  const ghlProfile = isGoHighLevelIntegration ? (selectedIntegration?.additional_data as GoHighLevelProfile) : null
  const availableBlogs = ghlProfile?.blogs ?? []
  const availableAuthors = ghlProfile?.authors ?? []

  return (
    <div className={Styles.container}>
      {/* Hidden file input for direct upload */}
      <VisuallyHidden>
        <input
          aria-label="Upload featured image"
          ref={fileInputRef}
          type="file"
          multiple={false}
          onChange={handleFileInputChange}
          accept="image/*"
        />
      </VisuallyHidden>

      <div className={Styles.descriptionContainer}>
        <Text variant="secondary">
          <span className={Styles.description}>
            Configuring metadata helps improve your blog post's SEO, visibility, and shareability across search engines
            and social media platforms.
          </span>
        </Text>
      </div>
      <div className={Styles.content}>
        <div
          className={cx(Styles.integrationsSelect, {
            [Styles.integrationsSelectNoIntegration]: blogIntegrations.length === 0,
          })}
        >
          <BlogIntegrationSelect />
          {blogIntegrations.length === 0 && <AlertTriangle color="var(--red-70)" />}
        </div>
      </div>
      <TextInputField
        wrapperClassName={Styles.inputField}
        label="Title"
        value={title}
        onChange={handleChangeTitle}
        size="lg"
        labelClassName={Styles.textFieldLabel}
      />
      <TextInputField
        wrapperClassName={Styles.inputField}
        label="Author"
        value={author}
        onChange={handleChangeAuthor}
        size="lg"
        labelClassName={Styles.textFieldLabel}
      />

      {isGoHighLevelIntegration && (
        <>
          <div className={Styles.categoryDropdownContainer}>
            <label className={Styles.textFieldLabel}>Blog</label>
            <div className={Styles.categoryDropdown}>
              <GoHighLevelBlogSelect
                blogs={availableBlogs}
                selectedBlogId={selectedBlogId}
                onBlogSelect={handleBlogSelect}
                disabled={isPostPublished}
              />
            </div>
          </div>

          <div className={Styles.categoryDropdownContainer}>
            <label className={Styles.textFieldLabel}>Author</label>
            <div className={Styles.categoryDropdown}>
              <GoHighLevelAuthorSelect
                authors={availableAuthors}
                selectedAuthorId={selectedAuthorId}
                onAuthorSelect={handleAuthorSelect}
                disabled={isPostPublished}
              />
            </div>
          </div>
        </>
      )}

      <div>
        <label className={Styles.textFieldLabel}>Summary</label>
        <div className={Styles.summaryInput}>
          <Input
            name="summary"
            placeholder="Enter a summary for your blog post"
            value={summary || ''}
            onChange={handleChangeSummary}
            size="md"
            isTextarea={true}
            rows={4}
          />
        </div>
      </div>
      <div className={Styles.featuredImageLabel}>
        <FeaturedMediaImage
          featuredImageUrl={featuredImageUrl}
          featuredImageAltText={featuredImageAltText}
          imageLoading={imageLoading}
          imageError={imageError}
          setImageLoading={setImageLoading}
          setImageError={setImageError}
          handleOpenFeaturedMediaModal={handleOpenFeaturedMediaModal}
          handleDirectUpload={handleDirectUpload}
          hasExistingImages={featuredImages.length > 0}
        />
      </div>
      <TextInputField
        wrapperClassName={Styles.inputField}
        label="Featured Image Alt Text"
        value={featuredImageAltText}
        onChange={handleChangeFeaturedImageAlt}
        size="lg"
        labelClassName={Styles.textFieldLabel}
        placeholder="Enter a description for your featured image"
      />
      <div className={Styles.categoryDropdownContainer}>
        <label className={Styles.textFieldLabel}>Categories</label>
        <div className={Styles.categoryDropdown}>
          <CategoryDropdown
            availableCategories={availableCategories}
            selectedCategories={selectedCategories}
            onCategoriesChange={handleCategoriesChange}
            onDropdownClose={handleCategoryDropdownClose}
            allowAddingCategories={allowAddingCategories}
          />
        </div>
      </div>
      <div className={Styles.tagsContainer}>
        <label className={Styles.textFieldLabel}>Tags</label>
        <div className={Styles.blogTagInputContainer}>
          <TagInput
            tags={tags}
            onChange={handleTagsChange}
            placeholder={'Add a tag'}
            useAddPill
            size="md"
            wrapperClassName={Styles.blogTagInputContainer}
          />
        </div>
      </div>
      <TextInputField
        wrapperClassName={Styles.inputField}
        label="URL Slug"
        value={slug}
        onChange={handleChangeSlug}
        size="lg"
        labelClassName={Styles.textFieldLabel}
      />
    </div>
  )
})
